import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useToast } from "../components/Toast";
import useIsMobile from "../Hooks/useIsMobile";
import PageLoader from "./Loader";
ChartJS.register(
  CategoryScale,
  ChartDataLabels,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);
const getCurrentMonth = () => {
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  return monthNames[new Date().getMonth()];
};
const BarGraphDialog = ({ open, onClose, kioskId }) => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState("monthwise");
  
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2020 + 1 }, (_, i) =>
    (2020 + i).toString()
  ).reverse();
  const [monthOptions] = useState([
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]);

  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [monthWiseCounts, setMonthWiseCounts] = useState([]);
  const [dayWiseCounts, setDayWiseCounts] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const token = localStorage.getItem("token");
  const Toast = useToast();

  // Get Start and End Dates
  const getStartAndEndDates = (year, month = null) => {
    const monthsMapping = {
      January: "01",
      February: "02",
      March: "03",
      April: "04",
      May: "05",
      June: "06",
      July: "07",
      August: "08",
      September: "09",
      October: "10",
      November: "11",
      December: "12",
    };

    if (month) {
      const monthNum = monthsMapping[month];
      const startDate = `01-${monthNum}-${year}`;
      const endDate = new Date(year, monthNum, 1)
        .toISOString()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("-");
      return { startDate, endDate };
    }

    const startDate = `01-01-${year}`;
    const endDate = `31-12-${year}`;
    return { startDate, endDate };
  };

  const fetchMonthWiseData = async () => {
    const { startDate, endDate } = getStartAndEndDates(selectedYear);
    try {
      setIsLoader(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/v1/analytics/get-month-wise-analytics-byKioskId`,
        {
          params: { startDate, endDate, kioskId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = response.data;
      
      if (data && data.monthWiseCounts) {
        // Sorting in correct order (January to December)
        const monthOrder = {
          January: 1,
          February: 2,
          March: 3,
          April: 4,
          May: 5,
          June: 6,
          July: 7,
          August: 8,
          September: 9,
          October: 10,
          November: 11,
          December: 12,
        };

        const sortedMonthWiseCounts = data.monthWiseCounts.sort(
          (a, b) =>
            monthOrder[a.month.split(" ")[0]] -
            monthOrder[b.month.split(" ")[0]]
        );

        setMonthWiseCounts(sortedMonthWiseCounts);
      } else {
        Toast.error("No matching kioskId in Data");
        setMonthWiseCounts([]);
      }

      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
      Toast.error("Error fetching month-wise data");
      console.error("Error fetching data:", error);
    }
  };

  // Fetch Day-wise Data
  const fetchDayWiseData = async () => {
    try {
      setIsLoader(true);
      const { startDate, endDate} = getStartAndEndDates(
        selectedYear,
        selectedMonth
      );
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/v1/analytics/get-day-wise-analytics-byKioskId`,
        {
          params: { startDate, endDate, kioskId},
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = response.data;

  

      if (data) {
        // Sort dayWiseCounts in ascending order based on date
        const sortedDayWiseCounts = data.dayWiseCounts.sort(
          (a, b) => {
            const dateA = new Date(a.date); // Convert to Date object
            const dateB = new Date(b.date);
            return dateA - dateB; // Sort in ascending order
          }
        );

        setDayWiseCounts(sortedDayWiseCounts);
      } else {
        Toast.error("No data available for selected month.");
        setDayWiseCounts([]);
      }

      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
      Toast.error("Error fetching day-wise data");
      console.error("Error fetching day-wise data:", error);
    }
  };

  useEffect(() => {
    if (open) {
      if (activeTab === "monthwise") {
        fetchMonthWiseData();
      } else {
        fetchDayWiseData();
      }
    } 
  }, [open, selectedYear, selectedMonth, activeTab, kioskId]);

  const monthLabels = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayLabels = Array.from({ length: 31 }, (_, i) => (i + 1).toString());

  const barHue = theme.Bars.secondary;
  const gridColor = theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
  const axisInk = theme.palette.text.secondary;

  const activeCounts = activeTab === "monthwise" ? monthWiseCounts : dayWiseCounts;
  const hasData = activeCounts.some((item) => item.count > 0);

  const chartData = {
    labels: activeTab === "monthwise" ? monthLabels : dayLabels,
    datasets: [
      {
        label: activeTab === "monthwise" ? "Monthly Scans" : "Daily Scans",
        data: activeCounts.map((item) => item.count),
        backgroundColor: barHue,
        hoverBackgroundColor: barHue,
        borderRadius: 4,
        borderSkipped: false,
        maxBarThickness: activeTab === "monthwise" ? 36 : 22,
      },
    ],
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: "1px solid",
          borderBottomColor: "divider",
          padding: "16px 20px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "action.hover",
              color: barHue,
            }}
          >
            <BarChartOutlinedIcon fontSize="small" />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18 }}>
            {activeTab === "monthwise" ? "Monthly Scans" : "Daily Scans"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          height: "550px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          padding: "20px",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          centered
          sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, fontWeight: 600 } }}
        >
          <Tab label="Month-wise" value="monthwise" />
          <Tab label="Day-wise" value="daywise" />
        </Tabs>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "12px",
            my: 2,
          }}
        >
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            size="small"
            sx={{ borderRadius: "8px", minWidth: 100 }}
          >
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>

          {activeTab === "daywise" && (
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              size="small"
              sx={{ borderRadius: "8px", minWidth: 140 }}
            >
              {monthOptions.map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>

        <Box sx={{ flexGrow: 1, position: "relative", minHeight: 0 }}>
          {hasData ? (
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  x: {
                    grid: { display: false },
                    ticks: { color: axisInk, font: { size: 11 } },
                  },
                  y: {
                    beginAtZero: true,
                    grid: { color: gridColor, drawTicks: false },
                    border: { display: false },
                    ticks: { color: axisInk, font: { size: 11 }, precision: 0 },
                  },
                },
                plugins: {
                  legend: { display: false },
                  datalabels: {
                    anchor: "end",
                    align: "top",
                    color: axisInk,
                    font: { weight: 600, size: 11 },
                    formatter: (value) => (value === 0 ? "" : value),
                  },
                  tooltip: {
                    backgroundColor: theme.palette.background.paper,
                    titleColor: theme.palette.text.primary,
                    bodyColor: theme.palette.text.secondary,
                    borderColor: "divider",
                    borderWidth: 1,
                    padding: 10,
                    displayColors: false,
                  },
                },
              }}
            />
          ) : (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                color: "text.secondary",
              }}
            >
              <BarChartOutlinedIcon sx={{ fontSize: 34, opacity: 0.4 }} />
              <Typography variant="body2">
                No scans recorded for this {activeTab === "monthwise" ? "year" : "month"}.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      {isLoader && <PageLoader />}
    </Dialog>
  );
};

export default BarGraphDialog;
